import React from 'react';
import styled, { css } from 'styled-components';
import { color } from '../../styles/mixins';
import { button14 } from '../../styles/typography';
import type { PolymorphicComponentProps } from '../../utils/types';
import { Loading } from './loading';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'gold';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

export interface InternalButtonProps {
  $fullWidth?: boolean;
  $loading?: boolean;
}

export const Button = <C extends React.ElementType = 'button'>(
  props: PolymorphicComponentProps<C, ButtonProps>
) => {
  const { as, children, loading, fullWidth, ...rest } = props;
  return (
    <ButtonHost
      as={as as any}
      $loading={loading}
      $fullWidth={fullWidth}
      {...rest}
    >
      {loading ? <Loading /> : children}
    </ButtonHost>
  );
};

export const ButtonHost = styled.button<ButtonProps & InternalButtonProps>`
  ${button14};
  text-align: center;
  border-radius: ${(props) => props.theme.border.radius}px;

  cursor: pointer;
  border: 1px solid transparent;
  background-color: transparent;
  white-space: pre;

  transition-property: background-color, border-color;
  transition-duration: ${(props) => props.theme.transition.duration};
  transition-timing-function: ${(props) => props.theme.transition.timing};

  ${(props) =>
    props.$fullWidth &&
    css`
      display: inline-flex;
      width: 100%;
      align-items: center;
      justify-content: center;
      white-space: initial;
    `}

  /* Loading */
  ${(props) =>
    props.loading &&
    css`
      display: inline-flex;
      align-items: center;
      min-height: 44px;
    `}

  /* Size */
  ${(props) => {
    switch (props.size) {
      case 'md':
        return css`
          padding: 10px 32px;
        `;
      case 'lg':
        return css`
          padding: 14px 48px;
          margin-top: -1px;
        `;
      case 'sm':
        return css`
          padding: 6px 16px;
        `;
    }
  }}

  /* Variant */
  ${(props) => {
    switch (props.variant) {
      case 'primary':
        return css`
          color: ${color('white')};
          background-color: ${color('blue100')};

          &:hover {
            background-color: ${color('blue60')};
          }

          &:active {
            background-color: ${color('blue80')};
          }

          &[disabled],
          &[disabled]:hover,
          &[disabled]:active {
            background-color: ${color('navy20')};
          }
        `;
      case 'secondary':
        return css`
          color: ${color('blue60')};
          background-color: transparent;
          border-color: ${color('gray50')};

          &:hover {
            border-color: ${color('blue60')};
          }
          &:active {
            border-color: ${color('blue80')};
          }
          &[disabled],
          &[disabled]:hover,
          &[disabled]:active {
            border-color: ${color('gray50')};
            color: ${color('gray70')};
          }
        `;
      case 'tertiary':
        return css`
          color: ${color('blue60')};
          background-color: transparent;

          &:hover {
            background-color: rgba(241, 241, 241, 0.4);
          }
          &:active {
            background-color: transparent;
          }
          &[disabled],
          &[disabled]:hover,
          &[disabled]:active {
            color: ${color('gray70')};
          }
        `;
      case 'gold':
        return css`
          color: ${color('white')};
          background-color: ${color('gold60')};

          &:hover {
            background-color: ${color('gold80')};
          }

          &:active {
            background-color: ${color('gold60')};
          }

          [disabled],
          &[disabled]:hover,
          &[disabled]:active {
            background-color: ${color('gold40')};
          }
        `;
    }
  }}
`;

ButtonHost.defaultProps = {
  variant: 'primary',
  size: 'md',
  $fullWidth: false,
} as ButtonProps;
